// 학생 인증 훅

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface Student {
  id: string;
  name: string;
  phone: string;
  organization_id?: string;
  organization_name?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  student: Student | null;
  error: string | null;
}

export function useStudentAuth() {
  const navigate = useNavigate();
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    student: null,
    error: null,
  });

  // 초기 인증 상태 확인
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('student_access_token');
    const storedStudent = localStorage.getItem('student_info');

    if (!token) {
      setState({
        isAuthenticated: false,
        isLoading: false,
        student: null,
        error: null,
      });
      return;
    }

    try {
      // /me API 호출하여 토큰 검증
      const response = await fetch(`${API_BASE_URL}/api/v1/unified-auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('인증 실패');
      }

      const data = await response.json();

      if (data.role !== 'student') {
        throw new Error('학생 계정이 아닙니다');
      }

      setState({
        isAuthenticated: true,
        isLoading: false,
        student: data.user,
        error: null,
      });
    } catch (error) {
      console.error('Auth check error:', error);

      // 저장된 정보로 복구 시도
      if (storedStudent) {
        try {
          const student = JSON.parse(storedStudent);
          setState({
            isAuthenticated: true,
            isLoading: false,
            student,
            error: null,
          });
          return;
        } catch {
          // 파싱 실패 시 로그아웃
        }
      }

      // 토큰 제거
      localStorage.removeItem('student_access_token');
      localStorage.removeItem('student_refresh_token');
      localStorage.removeItem('student_info');

      setState({
        isAuthenticated: false,
        isLoading: false,
        student: null,
        error: '인증이 만료되었습니다.',
      });
    }
  };

  const signOut = useCallback(async () => {
    localStorage.removeItem('student_access_token');
    localStorage.removeItem('student_refresh_token');
    localStorage.removeItem('student_info');
    localStorage.removeItem('unified_role');

    setState({
      isAuthenticated: false,
      isLoading: false,
      student: null,
      error: null,
    });

    navigate('/login', { replace: true });
  }, [navigate]);

  return {
    ...state,
    student: state.student,
    signOut,
    refreshAuth: checkAuth,
  };
}
